import type {TemporaryAgencySerializer} from "@/generated/DjangoTypes";
import {create, type TSetter} from "@/store/createStore";
import useRegistrationStore from "@/store/registration";
import type {TCurrency, TLearnAboutUsOption, TPackagePaymentPeriodType} from "@/store/types";
import {withStreetNumberCheck} from "@/utils/addressUtils";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AddressFieldsSchema = z.object({
  address: z
    .string({error: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error"),
  postal_code: z
    .string({error: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  city: z
    .string({error: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
  country: z.string().nullish(),
});

const AddressSchema = withStreetNumberCheck({countryKey: "country", schema: AddressFieldsSchema, streetKey: "address"});

const RegisterAgencyFieldsSchema = z
  .object({
    agency_name: z.string().min(1, "multi_account:agency_name_required"),
    website: z.url("Website url is not valid").min(1, "Website is required"),
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Surname is required"),
  })
  .extend(AddressFieldsSchema.shape);

const RegisterAgencySchema = withStreetNumberCheck({
  countryKey: "country",
  schema: RegisterAgencyFieldsSchema,
  streetKey: "address",
});

const DetailsSchema = z.object({
  phoneNumber: z.string().min(1, "incorrect_phone_number_length"),
});

const SetUpMultiAccountSchema = withStreetNumberCheck({
  countryKey: "country",
  schema: RegisterAgencyFieldsSchema.omit({first_name: true, last_name: true}),
  streetKey: "address",
});

export type TCreateAgencyFormState = Omit<
  TemporaryAgencySerializer,
  "country" | "currency" | "id" | "large_brand" | "package_type"
> & {
  country: string;
  credits?: number | null;
  currency: TCurrency;
  influencer_fees: {pro?: number | null; regular?: number | null};
  large_brand: boolean | null;
  package_type: TPackagePaymentPeriodType;
  place_description?: string | null;
  place_id: string | null;
};

type TDetailsState = {
  logo: File | null;
  profilePicture: File | null;
  username: string;
  password: string;
  howDidYouHearAboutUs: TLearnAboutUsOption | null;
  howDidYouHearAboutUsExtra: string;
  phoneNumber: string;
};

const initialFormState: TCreateAgencyFormState = {
  first_name: "",
  last_name: "",
  agency_name: "",
  address: "",
  city: "",
  postal_code: "",
  website: "",
  main_user_email: "",
  large_brand: null,
  country: "Switzerland",
  place_description: null,
  place_id: null,
  price_per_year: 0,
  price_per_month: 0,
  credits: 0,
  currency: "CHF",
  influencer_fees: {pro: null, regular: null},
  influencers_per_campaign: null,
  invitations_per_campaign: null,
  buyouts_per_year: null,
  number_of_campaigns: null,
  number_of_seats: null,
  package_type: "MONTHLY",
  sales_email: "",
  relationship_manager_email: "",
  extra_campaign_price: null,
  extra_seat_price: null,
};

const initialDetailsState: TDetailsState = {
  logo: null,
  profilePicture: null,
  username: "",
  password: "",
  howDidYouHearAboutUs: null,
  howDidYouHearAboutUsExtra: "",
  phoneNumber: "",
};

type TState = {
  form: TCreateAgencyFormState;
  details: TDetailsState;
  errors: Record<string, string | string[]> | null;
  setter: TSetter<TState>;
  validateAddress(): boolean;
  validateRegisterAgencySchema(): boolean;
  validateSetUpMultiAccountSchema(): boolean;
  validateDetailsSchema(): boolean;
  setPhoneNumber(value: string): void;
  processErrorIssues(issues: z.ZodIssue[]): void;
  reset(): void;
};

const useCreateAgencyStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        form: initialFormState,
        details: initialDetailsState,
        errors: null,
        setter: fn => set(fn, false, "useCreateAgencyStore/setter"),
        processErrorIssues: issues => {
          issues.map(issue =>
            set(state => {
              if (state.errors) {
                state.errors[issue.path[0].toString()] = [issue.message];
              } else {
                state.errors = {[issue.path[0]]: [issue.message]};
              }
            }),
          );
        },
        validateAddress: () => {
          const result = AddressSchema.safeParse(get().form);

          set(state => {
            if (!!state.errors) {
              state.errors.address = [];
              state.errors.city = [];
              state.errors.postal_code = [];
            }
          });

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        validateRegisterAgencySchema: () => {
          useRegistrationStore.getState().validateCredentials();
          const result = RegisterAgencySchema.safeParse(get().form);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return get().validateDetailsSchema() && result.success && !useRegistrationStore.getState().errors.password;
        },
        validateDetailsSchema: () => {
          const result = DetailsSchema.safeParse(get().details);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        validateSetUpMultiAccountSchema: () => {
          const result = SetUpMultiAccountSchema.safeParse(get().form);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        setPhoneNumber: value => {
          set(
            state => {
              state.details.phoneNumber = value;

              if (state.errors) {
                state.errors.phoneNumber = [];
              }
            },
            false,
            "useCreateAgencyStore/setPhoneNumber",
          );
        },
        reset: () =>
          set(() => ({
            form: initialFormState,
            details: initialDetailsState,
            isSeats: false,
          })),
      }),
      {name: "useCreateAgencyStore"},
    ),
  ),
);

export {useCreateAgencyStore};
